/**
 * UnitsPage Component
 * Lists all units with filtering — mobile-first card layout.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTrackEvent, useTrackFeature, ANALYTICS_EVENTS } from '@resida/analytics';
import { Plus, Building2 } from 'lucide-react';
import {
  listUnits,
  listProperties,
  createUnit,
  type UnitWithRelations,
  type PropertyWithStats,
  type CreateUnitData,
} from '../lib/api';
import { Modal } from '../components/Modal';
import { PageHeader, EmptyState, StatusBadge } from '../components/layout/index';
import { Button } from '../components/ui/button';
import { AddressAutocompleteInput, type AddressSuggestion } from '../components/AddressAutocompleteInput';
import { Skeleton } from '../components/ui/skeleton';
import { DEFAULT_UNIT_NUMBER, formatUnitLabel, realUnitNumber } from '@resida/shared';

type OccupancyFilter = 'all' | 'available' | 'occupied';

interface UnitFormData {
  propertyId: string;
  unitNumber: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
  floorNumber: string;
  bedrooms: string;
  bathrooms: string;
  squareFeet: string;
  notes: string;
}

const initialFormData: UnitFormData = {
  propertyId: '',
  unitNumber: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zipCode: '',
  floorNumber: '',
  bedrooms: '1',
  bathrooms: '1',
  squareFeet: '',
  notes: '',
};

export function UnitsPage() {
  const navigate = useNavigate();
  const track = useTrackEvent();
  useTrackFeature('units', 'landlord');
  const [searchParams, setSearchParams] = useSearchParams();

  const [units, setUnits] = useState<UnitWithRelations[]>([]);
  const [properties, setProperties] = useState<PropertyWithStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [formData, setFormData] = useState<UnitFormData>(initialFormData);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [occupancyFilter, setOccupancyFilter] = useState<OccupancyFilter>('all');

  // Address autocomplete state
  const handleAddressSuggestionSelect = (suggestion: AddressSuggestion) => {
    setFormData((prev) => ({
      ...prev,
      address1: suggestion.address1,
      city: suggestion.city,
      state: suggestion.state,
      zipCode: suggestion.zipCode,
      // address2 is intentionally NOT touched
    }));
  };

  const selectedPortfolioId = searchParams.get('portfolioId') || '';
  const selectedPropertyId = searchParams.get('propertyId') || '';

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const filters: { portfolioId?: string; propertyId?: string } = {};
      if (selectedPortfolioId) filters.portfolioId = selectedPortfolioId;
      if (selectedPropertyId) filters.propertyId = selectedPropertyId;

      const [unitsRes, propertiesRes] = await Promise.all([
        listUnits(Object.keys(filters).length > 0 ? filters : undefined),
        listProperties(),
      ]);

      setUnits(unitsRes.units);
      setProperties(propertiesRes.properties);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load units');
    } finally {
      setIsLoading(false);
    }
  }, [selectedPortfolioId, selectedPropertyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
      if (key === 'portfolioId') {
        params.delete('propertyId');
      }
    } else {
      params.delete(key);
    }
    setSearchParams(params);
  };

  const filteredProperties = useMemo(() => {
    if (!selectedPortfolioId) return properties;
    return properties.filter((p) => p.portfolioId === selectedPortfolioId);
  }, [properties, selectedPortfolioId]);

  const filteredUnits = useMemo(() => {
    if (occupancyFilter === 'all') return units;
    return units.filter((u) => u.occupancy === occupancyFilter);
  }, [units, occupancyFilter]);

  const handleFormChange = (field: keyof UnitFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const selectedProperty = properties.find(p => p.id === formData.propertyId);
    const isSingleFamily = selectedProperty?.propertyType === 'single_family';

    const data: CreateUnitData = {
      propertyId: formData.propertyId,
      unitNumber: isSingleFamily ? DEFAULT_UNIT_NUMBER : (formData.unitNumber.trim() || undefined),
      address1: formData.address1.trim(),
      address2: formData.address2.trim() || undefined,
      city: formData.city.trim(),
      state: formData.state.trim(),
      zipCode: formData.zipCode.trim(),
      bedrooms: parseInt(formData.bedrooms, 10),
      bathrooms: formData.bathrooms,
      sqft: formData.squareFeet ? parseInt(formData.squareFeet, 10) : undefined,
      metadata: formData.notes.trim() ? { notes: formData.notes.trim() } : {},
    };

    try {
      setIsCreating(true);
      setCreateError(null);
      const response = await createUnit(data);
      track(ANALYTICS_EVENTS.ONBOARDING_STEP_COMPLETED, { step: 'unit' });
      setIsCreateModalOpen(false);
      setFormData(initialFormData);
      navigate(`/units/${response.unit.id}`);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create unit');
    } finally {
      setIsCreating(false);
    }
  };

  const openCreateModal = () => {
    setFormData({
      ...initialFormData,
      propertyId: selectedPropertyId || (properties[0]?.id || ''),
    });
    setCreateError(null);
    setIsCreateModalOpen(true);
  };

  if (isLoading) {
    return <UnitsPageSkeleton />;
  }

  const occupancyChips: { value: OccupancyFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'occupied', label: 'Occupied' },
    { value: 'available', label: 'Vacant' },
  ];

  const inputClass = "w-full px-3 py-2.5 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring box-border";
  const labelClass = "block mb-1 text-sm font-medium text-foreground";

  return (
    <div className="max-w-4xl mx-auto pb-6">
      <div className="px-[var(--page-padding-x)]">
        <PageHeader
          title="Units"
          subtitle={`${filteredUnits.length} unit${filteredUnits.length !== 1 ? 's' : ''}`}
          action={
            <Button
              onClick={openCreateModal}
              disabled={properties.length === 0}
              size="sm"
            >
              <Plus className="h-4 w-4 md:mr-1" />
              <span className="hidden md:inline">Add Unit</span>
            </Button>
          }
        />
      </div>

      {error && (
        <div className="mx-[var(--page-padding-x)] mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm" role="alert">
          <p className="text-destructive">{error}</p>
          <Button variant="outline" size="sm" onClick={fetchData} className="mt-2">Retry</Button>
        </div>
      )}

      {/* Filters */}
      <div className="px-[var(--page-padding-x)] mb-4 space-y-3">
        {/* Occupancy chips */}
        <div className="flex gap-1.5 overflow-x-auto scrollbar-none">
          {occupancyChips.map((chip) => (
            <button
              key={chip.value}
              type="button"
              onClick={() => setOccupancyFilter(chip.value)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                occupancyFilter === chip.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-accent'
              }`}
            >
              {chip.label}
            </button>
          ))}
        </div>

        {/* Property dropdown */}
        <div className="flex gap-2 flex-wrap">
          <select
            value={selectedPropertyId}
            onChange={(e) => handleFilterChange('propertyId', e.target.value)}
            className="text-sm border border-input rounded-md px-2.5 py-1.5 bg-background min-h-[36px]"
            aria-label="Filter by property"
          >
            <option value="">All Properties</option>
            {filteredProperties.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Content */}
      {properties.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No Properties Yet"
          description="Create a property first before adding units."
          action={
            <Button onClick={() => navigate('/properties')}>Go to Properties</Button>
          }
        />
      ) : filteredUnits.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No Units Found"
          description={
            selectedPropertyId || selectedPortfolioId || occupancyFilter !== 'all'
              ? 'No units match the current filters.'
              : 'Add your first unit to get started.'
          }
          action={
            <Button onClick={openCreateModal}>Add Unit</Button>
          }
        />
      ) : (
        <div className="flex flex-col">
          {filteredUnits.map((unit) => {
            const hasRealUnit = realUnitNumber(unit.unitNumber) !== null;
            const unitTitle = formatUnitLabel(unit.unitNumber)
              ?? (unit.propertyName ?? unit.property?.name ?? 'Single Family');
            const propertyLine = hasRealUnit
              ? (unit.propertyName ?? unit.property?.name)
              : null;
            const addressLine = `${unit.address1}${unit.address2 ? `, ${unit.address2}` : ''}, ${unit.city}, ${unit.state} ${unit.zipCode}`;
            const specs = [
              unit.bedrooms != null ? `${unit.bedrooms} bed` : null,
              unit.bathrooms != null ? `${unit.bathrooms} bath` : null,
              unit.sqft ? `${unit.sqft.toLocaleString()} sqft` : null,
            ].filter(Boolean).join(' · ');

            return (
              <button
                key={unit.id}
                type="button"
                className="flex items-center gap-3 px-[var(--page-padding-x)] py-3 min-h-[48px] border-b border-border last:border-b-0 hover:bg-accent transition-colors text-left w-full cursor-pointer"
                onClick={() => navigate(`/units/${unit.id}`)}
                data-testid={`unit-item-${unit.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground truncate">{unitTitle}</p>
                    <StatusBadge
                      status={unit.occupancy === 'available' ? 'vacant' : 'occupied'}
                      size="sm"
                    />
                  </div>
                  {propertyLine && (
                    <p className="text-xs text-muted-foreground truncate">{propertyLine}</p>
                  )}
                  <p className="text-xs text-muted-foreground truncate">{addressLine}</p>
                  {specs && (
                    <p className="text-xs text-muted-foreground mt-0.5">{specs}</p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Create Modal — kept as-is for form field IDs */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Add New Unit"
        width="large"
      >
        <form onSubmit={handleCreateSubmit}>
          {createError && (
            <div className="p-3 mb-4 bg-destructive/5 border border-destructive/30 rounded-md text-sm text-destructive">
              {createError}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 mb-0">
            <div className="flex-1 mb-4">
              <label htmlFor="propertyId" className={labelClass}>Property *</label>
              <select
                id="propertyId"
                value={formData.propertyId}
                onChange={(e) => handleFormChange('propertyId', e.target.value)}
                className={inputClass + " bg-card"}
                required
              >
                <option value="">Select a property</option>
                {properties.map((property) => (
                  <option key={property.id} value={property.id}>
                    {property.name}
                  </option>
                ))}
              </select>
            </div>
            {(() => {
              const selectedProperty = properties.find(p => p.id === formData.propertyId);
              const isSingleFamily = selectedProperty?.propertyType === 'single_family';
              return !isSingleFamily ? (
                <>
                  <div className="w-full sm:w-[100px] mb-4">
                    <label htmlFor="unitNumber" className={labelClass}>Unit #</label>
                    <input
                      id="unitNumber"
                      type="text"
                      value={formData.unitNumber}
                      onChange={(e) => handleFormChange('unitNumber', e.target.value)}
                      placeholder="101"
                      className={inputClass}
                    />
                  </div>
                  <div className="w-full sm:w-[100px] mb-4">
                    <label htmlFor="floorNumber" className={labelClass}>Floor</label>
                    <input
                      id="floorNumber"
                      type="number"
                      value={formData.floorNumber}
                      onChange={(e) => handleFormChange('floorNumber', e.target.value)}
                      placeholder="1"
                      className={inputClass}
                      min="0"
                    />
                  </div>
                </>
              ) : null;
            })()}
          </div>

          <div className="mb-4">
            <label htmlFor="address1" className={labelClass}>Address Line 1 *</label>
            <AddressAutocompleteInput
              id="address1"
              data-testid="unit-address1-input"
              value={formData.address1}
              onChange={(v) => handleFormChange('address1', v)}
              onSuggestionSelect={handleAddressSuggestionSelect}
              placeholder="123 Main Street"
              className={inputClass}
              required
            />
          </div>

          <div className="mb-4">
            <label htmlFor="address2" className={labelClass}>Address Line 2</label>
            <input
              id="address2"
              type="text"
              value={formData.address2}
              onChange={(e) => handleFormChange('address2', e.target.value)}
              placeholder="Apt 4B, Unit 101, etc."
              className={inputClass}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-0">
            <div className="flex-1 mb-4">
              <label htmlFor="city" className={labelClass}>City *</label>
              <input
                id="city"
                type="text"
                value={formData.city}
                onChange={(e) => handleFormChange('city', e.target.value)}
                placeholder="San Francisco"
                className={inputClass}
                required
              />
            </div>
            <div className="w-full sm:w-[100px] mb-4">
              <label htmlFor="state" className={labelClass}>State *</label>
              <input
                id="state"
                type="text"
                value={formData.state}
                onChange={(e) => handleFormChange('state', e.target.value)}
                placeholder="CA"
                className={inputClass}
                maxLength={2}
                required
              />
            </div>
            <div className="w-full sm:w-[100px] mb-4">
              <label htmlFor="zipCode" className={labelClass}>ZIP Code *</label>
              <input
                id="zipCode"
                type="text"
                value={formData.zipCode}
                onChange={(e) => handleFormChange('zipCode', e.target.value)}
                placeholder="94102"
                className={inputClass}
                maxLength={10}
                required
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-0">
            <div className="flex-1 mb-4">
              <label htmlFor="bedrooms" className={labelClass}>Bedrooms *</label>
              <input
                id="bedrooms"
                type="number"
                value={formData.bedrooms}
                onChange={(e) => handleFormChange('bedrooms', e.target.value)}
                className={inputClass}
                min="0"
                required
              />
            </div>
            <div className="flex-1 mb-4">
              <label htmlFor="bathrooms" className={labelClass}>Bathrooms *</label>
              <input
                id="bathrooms"
                type="text"
                value={formData.bathrooms}
                onChange={(e) => handleFormChange('bathrooms', e.target.value)}
                className={inputClass}
                min="0"
                step="0.5"
                required
              />
            </div>
            <div className="flex-1 mb-4">
              <label htmlFor="squareFeet" className={labelClass}>Square Feet</label>
              <input
                id="squareFeet"
                type="number"
                value={formData.squareFeet}
                onChange={(e) => handleFormChange('squareFeet', e.target.value)}
                placeholder="800"
                className={inputClass}
                min="0"
              />
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="notes" className={labelClass}>Notes</label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleFormChange('notes', e.target.value)}
              placeholder="Any additional notes about this unit..."
              className={inputClass + " resize-y"}
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-4 mt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsCreateModalOpen(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="unit-form-submit"
              disabled={
                isCreating ||
                !formData.propertyId ||
                !formData.address1.trim() ||
                !formData.city.trim() ||
                !formData.state.trim() ||
                !formData.zipCode.trim()
              }
            >
              {isCreating ? 'Creating...' : 'Create Unit'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function UnitsPageSkeleton() {
  return (
    <div className="max-w-4xl mx-auto pb-6">
      <div className="px-[var(--page-padding-x)] pt-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-4 w-20 mt-2" />
      </div>
      <div className="px-[var(--page-padding-x)] mt-4 space-y-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    </div>
  );
}

export default UnitsPage;
